import React from "react";
import {
  Input,
  SelectItem,
  Textarea,
  type SharedSelection,
} from "@heroui/react";
import type {
  FieldDefinition,
  FieldOption,
  ProfileId,
} from "../../../types/types";
import {
  limitDateInputYear,
  readLimitedDateInputValue,
  resolveDateInputMax,
} from "../../../utils/common/dateInput";
import { resolveDateFieldBounds } from "../../../utils/deals/dateFieldRules";
import { normalizeOptionsSource } from "../../../utils/deals/dealNormalizers";
import { getDisabledUniqueAcrossItemsValues } from "../../../utils/common/uniqueAcrossItems";
import { FighterSearchField } from "./fields/fighterSearchField";
import { CountryDropdown, CityDropdown } from "./fields/countryCityDropdowns";
import { FileUploadField } from "./fields/fileUploadField";
import type { FormValue, TranslationParams } from "./dealFormTypes";
import AppSelect from "../../../components/common/appSelect";

export { prefetchFighters } from "./fields/fighterSearchField";
export { prefetchCountries } from "./fields/countryCityDropdowns";


type FormValues = Record<string, FormValue>;

type Fighter = {
  id: ProfileId;
  firstName?: string;
  lastName?: string;
};

type LoadedFieldOption = {
  key: string;
  label: string;
};

export type LoadedOptionsBySource = Record<string, LoadedFieldOption[]>;

export type TranslationFn = (
  key?: string,
  fallbackOrOptions?: TranslationParams,
) => string;

type FieldHelpers = {
  getFormValue?: (stepId: string, fieldKey: string) => FormValue;
  updateFieldValue?: (
    stepId: string,
    fieldKey: string,
    value: FormValue,
  ) => void;
  errors?: Record<string, string | undefined>;
  formData?: FormValues;
  optionsBySource?: LoadedOptionsBySource;
  loadingOptionsBySource?: Record<string, boolean>;
  availableFighters?: Fighter[];
  isLockedField?: (stepId: string, fieldKey: string) => boolean;
  repeatableItems?: FormValues[];
  repeatableIndex?: number;
  tr?: TranslationFn;
};


const boxStyle =
  "rounded-2xl border border-[#E4E4E7] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] hover:border-[#D4D4D8] transition-all duration-150";
const singleLineSize = "h-10 min-h-0 px-3";
const inputFocusStyle =
  "focus-within:border-[#0080FF] focus-within:ring-2 focus-within:ring-[#0080FF]/20";
const selectFocusStyle =
  "data-[focus=true]:border-[#0080FF] data-[focus=true]:ring-2 data-[focus=true]:ring-[#0080FF]/20";
const invalidStyle =
  "data-[invalid=true]:!border-red-400 data-[invalid=true]:!bg-red-50 data-[invalid=true]:focus-within:!border-red-500 data-[invalid=true]:focus-within:!ring-red-100";
const textStyle = "text-[14px] text-slate-800";

const inputClassNames = {
  inputWrapper: `${boxStyle} ${singleLineSize} ${inputFocusStyle} ${invalidStyle}`,
  input: `${textStyle} placeholder:text-slate-400`,
};

export const selectClassNames = {
  trigger: `${boxStyle} ${singleLineSize} ${selectFocusStyle} ${invalidStyle}`,
  value: textStyle,
  listboxWrapper: "shadow-lg border border-slate-100 rounded-xl",
};

export const textareaClassNames = {
  inputWrapper: `${boxStyle} px-3 py-2.5 ${inputFocusStyle} ${invalidStyle}`,
  input: `${textStyle} placeholder:text-slate-400`,
};


export function humanizeLabel(raw?: string): string {
  if (!raw) return "";
  const spaced = raw
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatDisplayLabel(label: string): string {
  const looksLikeCode =
    /[_-]/.test(label) ||
    /[a-z][A-Z]/.test(label) ||
    /^[A-Z0-9_]+$/.test(label);
  return looksLikeCode ? humanizeLabel(label) : label;
}

export function getOptionKey(option: FieldOption): string {
  return option.value;
}

export function getOptionLabel(option: FieldOption): string {
  return option.label;
}

export function normalizeApiOptions(values: string[]): LoadedFieldOption[] {
  return values.map((value) => ({ key: value, label: value }));
}

function getOptionsFromFormData(
  source: Array<FormValues | string> | FormValues | undefined,
  leafKey: string,
): LoadedFieldOption[] {
  if (Array.isArray(source)) {
    const options: LoadedFieldOption[] = [];
    const seenKeys = new Set<string>();

    for (const item of source) {
      if (item == null) continue;

      const rawKey =
        typeof item === "string"
          ? item
          : (item[leafKey] ?? item.value ?? item.key ?? item.id);
      if (rawKey === undefined || rawKey === null || rawKey === "") continue;

      const key = String(rawKey);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);

      const label =
        typeof item === "string"
          ? humanizeLabel(key)
          : String(item.label ?? item.title ?? item.text ?? key);
      options.push({ key, label });
    }
    return options;
  }

  if (source && typeof source === "object") {
    const rawKey = source[leafKey];
    if (rawKey === undefined || rawKey === null || rawKey === "") return [];
    return [{ key: String(rawKey), label: humanizeLabel(String(rawKey)) }];
  }

  return [];
}

export function buildOptions(
  field: FieldDefinition,
  contextData?: FormValues,
  fullFormData?: FormValues,
  optionsBySource?: LoadedOptionsBySource,
): LoadedFieldOption[] {
  const merged = new Map<string, string>();

  for (const option of field.options ?? []) {
    if (option.visibleWhen && contextData) {
      const isVisible = Object.entries(option.visibleWhen).every(
        ([key, expected]) => contextData[key] === expected,
      );
      if (!isVisible) continue;
    }
    merged.set(option.value, humanizeLabel(option.label));
  }

  const source = normalizeOptionsSource(field.optionsSource);
  if (source) {
    for (const option of optionsBySource?.[source] ?? []) {
      merged.set(option.key, option.label);
    }
  }

  if (field.optionsFrom) {
    const pathParts = field.optionsFrom.trim().split(".").filter(Boolean);

    if (pathParts.length > 0) {
      const rootKey = pathParts[0];
      const leafKey = pathParts[pathParts.length - 1];
      const rootValue = contextData?.[rootKey] ?? fullFormData?.[rootKey];

      for (const option of getOptionsFromFormData(
        rootValue as Array<FormValues | string> | FormValues | undefined,
        leafKey,
      )) {
        merged.set(option.key, option.label);
      }
    }
  }

  return Array.from(merged, ([key, label]) => ({ key, label }));
}


export function buildItemDefaults(fields: FieldDefinition[]): FormValues {
  const defaults: FormValues = {};
  for (const field of fields) {
    if (field.default !== undefined) defaults[field.key] = field.default;
  }
  return defaults;
}

function getValueByPath(obj: FormValues, path: string): FormValue {
  let current: FormValue = obj;
  for (const key of path.split(".")) {
    current = (current as FormValues | null | undefined)?.[key];
  }
  return current;
}

export function normalizeComponentType(raw?: unknown): string {
  if (!raw && raw !== 0) return "";
  return String(raw)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");
}

// Backend sends yyyy-mm-dd. Older data can come as dd-mm-yyyy.
function normalizeDateString(value?: unknown): string {
  if (!value) return "";
  const raw = String(value).trim();
  const parts = raw.split("-");
  if (parts.length !== 3 || parts[0].length === 4) return raw;

  const [day, month, year] = parts;
  if (!day || !month || !year) return raw;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

// ─── Visible / required rules ────────────────────────────────────────────────

// Looks for a field value in the repeatable item first, then the current step, then every step
function findValueByKey(
  key: string,
  stepData: FormValues,
  itemData?: FormValues,
  fullFormData?: FormValues,
): FormValue {
  if (itemData?.[key] !== undefined) return itemData[key];
  if (stepData[key] !== undefined) return stepData[key];

  for (const step of Object.values(fullFormData ?? {})) {
    if (!step || typeof step !== "object" || Array.isArray(step)) continue;
    if (step[key] !== undefined) return step[key];
  }
  return undefined;
}

function matchesConditions(
  conditions: FormValues | undefined,
  stepData: FormValues,
  itemData?: FormValues,
  fullFormData?: FormValues,
): boolean {
  if (!conditions) return true;

  return Object.entries(conditions).every(([key, expected]) => {
    const actual = findValueByKey(key, stepData, itemData, fullFormData);
    return Array.isArray(expected)
      ? expected.includes(actual)
      : actual === expected;
  });
}

export function isFieldVisible(
  field: FieldDefinition,
  stepData: FormValues,
  itemData?: FormValues,
  fullFormData?: FormValues,
): boolean {
  return matchesConditions(field.visibleWhen, stepData, itemData, fullFormData);
}

function isFieldRequired(
  field: FieldDefinition,
  stepData: FormValues,
  itemData?: FormValues,
  fullFormData?: FormValues,
): boolean {
  if (field.validation?.required) return true;

  const requiredWhen = field.validation?.requiredWhen;
  if (!requiredWhen) return false;
  return matchesConditions(requiredWhen, stepData, itemData, fullFormData);
}

// ─── Small UI pieces ─────────────────────────────────────────────────────────

export const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required,
}) => (
  <label className="block text-xs font-medium text-[#71717A] mb-1.5 leading-none font-['Plus_Jakarta_Sans']">
    {formatDisplayLabel(label)}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const FieldError: React.FC<{ error?: string }> = ({ error }) =>
  error ? (
    <p className="text-[12px] text-red-500 mt-1 flex items-center gap-1">
      <svg
        className="w-3.5 h-3.5 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {error}
    </p>
  ) : null;

const FieldWrapper: React.FC<{
  label: string;
  required: boolean;
  error?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ label, required, error, className = "", children }) => (
  <div className={`flex flex-col ${className}`}>
    <FieldLabel label={label} required={required} />
    {children}
    <FieldError error={error} />
  </div>
);

interface NativeDateInputProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  isInvalid?: boolean;
  ariaLabel?: string;
}

const NativeDateInput: React.FC<NativeDateInputProps> = ({
  value,
  onChange,
  min,
  max,
  isInvalid,
  ariaLabel,
}) => {
  const borderColor = isInvalid
    ? "border-red-400 hover:border-red-500 focus:border-red-500 focus:ring-red-100"
    : "border-slate-200 hover:border-slate-400 focus:border-blue-500 focus:ring-blue-100";

  return (
    <input
      type="date"
      value={limitDateInputYear(value)}
      min={min}
      max={resolveDateInputMax(max)}
      aria-label={ariaLabel}
      aria-invalid={isInvalid ? true : undefined}
      pattern="\d{4}-\d{2}-\d{2}"
      onInput={(e) => readLimitedDateInputValue(e.currentTarget)}
      onChange={(e) => onChange(readLimitedDateInputValue(e.currentTarget))}
      className={[
        "w-full h-10 rounded-lg border bg-white px-3 text-[14px] text-slate-800",
        "shadow-none outline-none transition-all duration-150 focus:ring-2",
        "[color-scheme:light]",
        borderColor,
      ].join(" ")}
    />
  );
};


function getDefaultNameKey(fieldKey: string): string {
  return fieldKey.toLowerCase().endsWith("id")
    ? fieldKey.replace(/id$/i, "Name")
    : `${fieldKey}Name`;
}

function resolveFighterName(
  stepId: string,
  field: FieldDefinition,
  contextData: FormValues,
  helpers?: FieldHelpers,
): string {
  const fieldKey = field.key;
  const nameKey = field.nameField || getDefaultNameKey(fieldKey);

  const coreData = (helpers?.formData?.core ?? {}) as Record<string, FormValue>;
  const stepFormData = (helpers?.formData?.[stepId] ?? {}) as Record<
    string,
    FormValue
  >;

  const allStepsData: FormValues = {};
  for (const step of Object.values(helpers?.formData ?? {})) {
    if (step && typeof step === "object" && !Array.isArray(step)) {
      Object.assign(allStepsData, step);
    }
  }

  const savedName =
    contextData[nameKey] ??
    coreData[nameKey] ??
    stepFormData[nameKey] ??
    allStepsData[nameKey] ??
    helpers?.getFormValue?.(stepId, nameKey);
  if (savedName) return String(savedName).trim();

  const fighterId =
    contextData[fieldKey] ??
    coreData[fieldKey] ??
    stepFormData[fieldKey] ??
    helpers?.getFormValue?.(stepId, fieldKey);
  const fighter = helpers?.availableFighters?.find(
    (item) => item.id === fighterId,
  );
  if (!fighterId || !fighter) return "";

  return [fighter.firstName, fighter.lastName].filter(Boolean).join(" ").trim();
}


export function renderSingleField(
  stepId: string,
  field: FieldDefinition,
  stepData: FormValues,
  itemData?: FormValues,
  onItemChange?: (key: string, value: FormValue) => void,
  helpers?: FieldHelpers,
): React.ReactNode {
  const safeStepData: FormValues =
    Array.isArray(stepData) || stepData == null ? {} : stepData;
  const contextData: FormValues =
    itemData !== undefined ? { ...safeStepData, ...itemData } : safeStepData;

  if (!isFieldVisible(field, safeStepData, itemData, helpers?.formData)) {
    return null;
  }

  // ── Current value ──
  const fieldKey = field.key;
  const fieldType = (field.type || "text").toLowerCase();

  let value: FormValue;
  if (itemData !== undefined) {
    value = getValueByPath(itemData, fieldKey);
  } else if (helpers?.getFormValue) {
    value = helpers.getFormValue(stepId, fieldKey);
  } else {
    value = safeStepData[fieldKey];
  }
  const normalizedValue =
    fieldType === "date" ? normalizeDateString(value) : value;

  // ── Shared values ──
  const tr = helpers?.tr ?? ((text?: string) => text ?? "");
  const rawLabel = field.label || fieldKey;
  const label = tr(rawLabel);
  const required = isFieldRequired(
    field,
    safeStepData,
    itemData,
    helpers?.formData,
  );
  const error =
    helpers?.errors?.[fieldKey] ?? helpers?.errors?.[`${stepId}.${fieldKey}`];

  // Saves values into the repeatable item (when there is one) or into the step form data
  const saveValues = (values: FormValues) => {
    const entries = Object.entries(values);

    if (onItemChange) {
      if (entries.length === 1) {
        onItemChange(entries[0][0], entries[0][1]);
      } else {
        onItemChange("__patch__", values);
      }
      return;
    }

    for (const [key, newValue] of entries) {
      helpers?.updateFieldValue?.(stepId, key, newValue);
    }
  };

  const handleChange = (newValue: FormValue) => {
    if (newValue === value) return;

    // Template onChange rules reset dependent fields (e.g. changing componentType clears the amount)
    const values: FormValues = { [fieldKey]: newValue };
    for (const rule of field.onChange ?? []) {
      if (rule.action === "reset") values[rule.target] = undefined;
    }
    saveValues(values);
  };

  const getOptions = () =>
    buildOptions(
      field,
      contextData,
      helpers?.formData,
      helpers?.optionsBySource,
    );

  // ── Locked field: shown as a disabled text box ──
  if (helpers?.isLockedField?.(stepId, fieldKey)) {
    const options = getOptions();
    const getOptionText = (rawValue: unknown) => {
      const text = String(rawValue ?? "");
      return options.find((option) => option.key === text)?.label || text;
    };

    let valueText = "";
    if (Array.isArray(normalizedValue)) {
      valueText = normalizedValue.map(getOptionText).filter(Boolean).join(", ");
    } else if (normalizedValue !== undefined && normalizedValue !== null) {
      valueText = getOptionText(normalizedValue);
    }

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <input
          type={fieldType === "date" ? "date" : "text"}
          value={valueText}
          disabled
          readOnly
          aria-label={label}
          className={[
            "h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3",
            "text-[14px] text-slate-800 shadow-none outline-none",
            "cursor-not-allowed disabled:opacity-100",
            fieldType === "date" ? "[color-scheme:light]" : "",
          ].join(" ")}
        />
      </FieldWrapper>
    );
  }

  // ── Fighter search ──
  if (fieldType === "fighter_search") {
    const storeSelectedAs = field.storeSelectedAs || "id";
    const nameKey =
      field.nameField ||
      (storeSelectedAs === "name" ? fieldKey : getDefaultNameKey(fieldKey));

    const stepValues = helpers?.formData?.[stepId] as Record<string, FormValue> | null | undefined;
    const coreValues = helpers?.formData?.core as Record<string, FormValue> | null | undefined;
    const currentId = (
      storeSelectedAs === "name"
        ? undefined
        : (value ?? stepValues?.[fieldKey] ?? coreValues?.[fieldKey])
    ) as number | string;

    const resolvedName = resolveFighterName(
      stepId,
      field,
      contextData,
      helpers,
    );
    const currentName =
      storeSelectedAs === "name"
        ? resolvedName || (value == null ? "" : String(value))
        : resolvedName;

    // The name is saved in its own field, unless the name field is the same as the main field
    const saveFighter = (storedValue: unknown, name: string | undefined) => {
      const values = { [fieldKey]: storedValue } as FormValues;
      if (nameKey !== fieldKey) values[nameKey] = name;
      saveValues(values);
    };

    const handleFighterChange = (
      id: number | string,
      name: string,
      isManual = false,
    ) => {
      if (!id && !isManual) {
        saveFighter(undefined, undefined);
        return;
      }
      const saveName = storeSelectedAs === "name" || isManual;
      saveFighter(saveName ? name : id, name);
    };

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <FighterSearchField
          currentId={currentId}
          currentName={currentName}
          placeholder={tr(field.placeholder) || tr("searchFighterByName")}
          tr={tr}
          allowManualEntry={field.allowManualEntry}
          isInvalid={!!error}
          onChange={handleFighterChange}
        />
      </FieldWrapper>
    );
  }

  // ── Country / City ──
  if (fieldKey === "country") {
    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <CountryDropdown
          value={(normalizedValue ?? "") as string}
          onChange={handleChange}
          isInvalid={!!error}
          tr={tr}
        />
      </FieldWrapper>
    );
  }

  if (fieldKey === "city") {
    const country = (contextData.country ??
      helpers?.getFormValue?.(stepId, "country") ??
      "") as string;

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <CityDropdown
          value={(normalizedValue ?? "") as string}
          country={country}
          onChange={handleChange}
          isInvalid={!!error}
          tr={tr}
        />
      </FieldWrapper>
    );
  }

  // ── File upload ──
  if (fieldType === "file_upload") {
    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <FileUploadField
          field={field}
          value={
            normalizedValue as
              | string
              | { fileUrl?: string; url?: string; fileName?: string; name?: string }
              | null
              | undefined
          }
          isInvalid={!!error}
          tr={tr}
          onChange={handleChange}
        />
      </FieldWrapper>
    );
  }

  // ── Select / Multi select ──
  if (
    fieldType === "select" ||
    fieldType === "multiselect" ||
    fieldType === "multi_select"
  ) {
    const isMulti = fieldType !== "select";
    const options = getOptions();

    const sourceKey = normalizeOptionsSource(field.optionsSource);
    const isOptionsLoading = !!(
      sourceKey && helpers?.loadingOptionsBySource?.[sourceKey]
    );

    // Saved values as strings. A single select has at most one.
    let savedValues: string[] = [];
    if (Array.isArray(normalizedValue)) {
      savedValues = normalizedValue
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map(String);
    } else if (normalizedValue !== undefined && normalizedValue !== null) {
      savedValues = normalizedValue === "" ? [] : [String(normalizedValue)];
    }

    // If some saved values are no longer valid options, only the valid ones stay selected
    const validValues = savedValues.filter((saved) =>
      options.some((option) => option.key === saved),
    );
    const selectedValues = validValues.length > 0 ? validValues : savedValues;
    const selectedKeys = new Set(selectedValues);

    // A saved value that is missing in the options is still shown as an option
    const selectOptions = options.map((option) => ({
      key: option.key,
      label: humanizeLabel(option.label || option.key),
    }));
    for (const saved of selectedValues) {
      if (!selectOptions.some((option) => option.key === saved)) {
        selectOptions.push({ key: saved, label: humanizeLabel(saved) });
      }
    }

    // Values already picked in other repeatable items can't be picked again
    const stepItems = helpers?.formData?.[stepId];
    const repeatableItems =
      helpers?.repeatableItems ?? (Array.isArray(stepItems) ? stepItems : []);
    const repeatableIndex =
      helpers?.repeatableIndex ??
      (itemData ? repeatableItems.indexOf(itemData) : -1);
    const disabledValues = getDisabledUniqueAcrossItemsValues(
      field,
      repeatableItems,
      repeatableIndex,
    );

    const handleSelectionChange = (keys: SharedSelection) => {
      if (keys === "all") {
        handleChange(selectOptions.map((option) => option.key));
        return;
      }

      const selected = Array.from(keys).map(String);
      const isDisabledValue =
        !isMulti &&
        selected[0] &&
        !selectedKeys.has(selected[0]) &&
        disabledValues.has(selected[0]);
      if (isDisabledValue) return;

      handleChange(isMulti ? selected : selected[0]);
    };

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <AppSelect
          size="sm"
          variant="bordered"
          selectionMode={isMulti ? "multiple" : undefined}
          selectedKeys={selectedKeys}
          isDisabled={isOptionsLoading}
          onSelectionChange={handleSelectionChange}
          placeholder={
            isOptionsLoading
              ? tr("loading")
              : (field.placeholder ??
                tr("selectPlaceholder", { label: humanizeLabel(rawLabel) }))
          }
          isInvalid={!!error}
          classNames={selectClassNames}
        >
          {selectOptions.length > 0 ? (
            selectOptions.map((option) => (
              <SelectItem
                key={option.key}
                className="text-[14px]"
                isDisabled={
                  !selectedKeys.has(option.key) &&
                  disabledValues.has(option.key)
                }
              >
                {tr(option.label)}
              </SelectItem>
            ))
          ) : (
            <SelectItem key="__none" className="text-[14px]" isDisabled>
              —
            </SelectItem>
          )}
        </AppSelect>
      </FieldWrapper>
    );
  }

  // ── Number ──
  if (fieldType === "number") {
    let min: number | undefined;
    if (field.min != null) min = Number(field.min);
    else if (field.validation?.min != null) min = Number(field.validation.min);

    let max: number | undefined;
    if (field.max != null) max = Number(field.max);
    else if (field.validation?.max != null) max = Number(field.validation.max);

    // Some amounts have a different max depending on the mode (e.g. percent of base is max 100)
    const maxWhen = field.validation?.maxWhen;
    if (maxWhen && contextData.mode === maxWhen.mode) {
      max = maxWhen.max;
    }

    const numberValue =
      normalizedValue != null && normalizedValue !== ""
        ? Number(normalizedValue)
        : null;

    let rangeError: string | undefined;
    if (numberValue !== null) {
      if (min != null && numberValue < min) {
        rangeError = tr("minimumValueIs", { value: min });
      } else if (max != null && numberValue > max) {
        rangeError = tr("maximumValueIs", { value: max });
      }
    }

    // A percentage must also give a minimum amount after it is applied to the base amount
    const minResolvedAmount = field.validation?.minResolvedAmount;
    let resolvedAmountError: string | undefined;

    if (minResolvedAmount && numberValue !== null) {
      const appliesWhen = minResolvedAmount.appliesWhen ?? {};
      const applies = Object.entries(appliesWhen).every(
        ([key, expected]) => contextData[key] === expected,
      );

      if (applies) {
        const isPercentOfBase = minResolvedAmount.resolveAs?.includes(
          "(value / 100) * core.baseAmount",
        );
        const baseAmount = Number(
          (helpers?.formData?.core as Record<string, FormValue> | null | undefined)?.baseAmount ??
            (contextData.core as Record<string, FormValue> | null | undefined)?.baseAmount ??
            0,
        );
        const resolvedAmount = isPercentOfBase
          ? (numberValue / 100) * baseAmount
          : numberValue;

        if (resolvedAmount < minResolvedAmount.amount) {
          resolvedAmountError =
            minResolvedAmount.message ??
            tr("resolvedComponentAmountMinimum", {
              amount: minResolvedAmount.amount.toFixed(2),
            });
        }
      }
    }

    const displayError = error ?? rangeError ?? resolvedAmountError;

    return (
      <FieldWrapper key={fieldKey} label={label} required={required}>
        <Input
          type="number"
          size="sm"
          variant="bordered"
          value={(normalizedValue ?? "") as string}
          onChange={(e) =>
            handleChange(
              e.target.value !== "" ? Number(e.target.value) : undefined,
            )
          }
          step={field.step ?? "1"}
          min={min != null ? String(min) : undefined}
          max={max != null ? String(max) : undefined}
          placeholder={field.placeholder ?? "0"}
          isInvalid={!!displayError}
          classNames={inputClassNames}
        />
        <div className="flex items-center justify-between mt-1">
          <FieldError error={displayError} />
          {max != null && !displayError && (
            <span className="text-[11px] text-slate-400 ml-auto">
              {tr("max", { max })}
            </span>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // ── Duration (minutes) ──
  if (fieldType === "duration") {
    const min =
      field.validation?.min != null ? Number(field.validation.min) : undefined;
    const max =
      field.validation?.max != null ? Number(field.validation.max) : undefined;

    const durationValue =
      normalizedValue != null && normalizedValue !== ""
        ? Number(normalizedValue)
        : null;
    const isOverMax =
      max !== undefined && durationValue !== null && durationValue > max;
    const isInvalid = Boolean(error || isOverMax);

    const maxMessage = field.validation?.maxMessage;
    const displayError = isOverMax && maxMessage ? tr(maxMessage) : error;

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={displayError}
      >
        <div className="flex items-center gap-0 w-full">
          <input
            type="number"
            value={(normalizedValue ?? "") as string}
            min={min}
            max={max}
            placeholder={tr("enterMinutes")}
            onChange={(e) =>
              handleChange(
                e.target.value !== "" ? Number(e.target.value) : undefined,
              )
            }
            className={[
              "flex-1 h-10 rounded-l-lg border bg-white px-3 text-[14px] text-slate-800",
              "outline-none transition-all duration-150 focus:ring-2",
              isInvalid
                ? "border-red-400 focus:border-red-500 focus:ring-red-100"
                : "border-slate-200 hover:border-slate-400 focus:border-blue-500 focus:ring-blue-100",
            ].join(" ")}
          />
          <div
            className={[
              "h-10 px-3 flex items-center rounded-r-lg border-t border-r border-b text-[13px] font-medium text-slate-500 bg-slate-50",
              isInvalid ? "border-red-400" : "border-slate-200",
            ].join(" ")}
          >
            {tr("minutes")}
          </div>
        </div>
      </FieldWrapper>
    );
  }

  // ── Checkbox ──
  if (fieldType === "checkbox") {
    return (
      <div key={fieldKey} className="flex items-center gap-2.5 mt-6">
        <input
          type="checkbox"
          id={fieldKey}
          checked={!!normalizedValue}
          onChange={(e) => handleChange(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 cursor-pointer"
        />
        <label
          htmlFor={fieldKey}
          className="text-[14px] text-slate-700 font-medium cursor-pointer select-none"
        >
          {label}
        </label>
      </div>
    );
  }

  // ── Date ──
  if (fieldType === "date") {
    const dateBounds = resolveDateFieldBounds(field, {
      formData: helpers?.formData,
      stepId,
      contextData,
    });

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        error={error}
      >
        <NativeDateInput
          value={(normalizedValue ?? "") as string}
          onChange={handleChange}
          min={dateBounds.min}
          max={dateBounds.max}
          isInvalid={!!error}
          ariaLabel={label}
        />
      </FieldWrapper>
    );
  }

  // ── Textarea ──
  if (fieldType === "textarea") {
    const maxLength = field.maxLength ?? field.validation?.maxLength;
    const colSpanClass = field.colSpan === 1 ? "col-span-1" : "col-span-full";

    return (
      <FieldWrapper
        key={fieldKey}
        label={label}
        required={required}
        className={colSpanClass}
      >
        <Textarea
          size="sm"
          variant="bordered"
          value={(normalizedValue ?? "") as string}
          onChange={(e) =>
            handleChange(e.target.value === "" ? undefined : e.target.value)
          }
          minRows={field.minRows ?? 3}
          maxLength={maxLength}
          placeholder={tr(field.placeholder)}
          isInvalid={!!error}
          classNames={textareaClassNames}
        />
        <div className="flex items-center justify-between mt-1">
          <FieldError error={error} />
          {maxLength && (
            <p className="text-[11px] text-slate-400 ml-auto">
              {String(normalizedValue ?? "").length} / {maxLength}
            </p>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // ── Radio / Switch / Boolean: shown as a select when the field has options ──
  if (
    fieldType === "radio" ||
    fieldType === "switch" ||
    fieldType === "boolean"
  ) {
    const options = getOptions();

    if (options.length > 0) {
      const savedValue =
        normalizedValue != null && normalizedValue !== ""
          ? String(normalizedValue)
          : "";
      const isValidOption = options.some((option) => option.key === savedValue);

      return (
        <FieldWrapper
          key={fieldKey}
          label={label}
          required={required}
          error={error}
        >
          <AppSelect
            size="sm"
            variant="bordered"
            selectedKeys={isValidOption ? new Set([savedValue]) : new Set()}
            onSelectionChange={(keys) => {
              if (keys === "all") return;
              handleChange(Array.from(keys).map(String)[0]);
            }}
            placeholder={tr(field.placeholder) || tr("selectPlaceholder")}
            isInvalid={!!error}
            classNames={selectClassNames}
          >
            {options.map((option) => (
              <SelectItem key={option.key} className="text-[14px]">
                {tr(option.label)}
              </SelectItem>
            ))}
          </AppSelect>
        </FieldWrapper>
      );
    }
  }

  return (
    <FieldWrapper
      key={fieldKey}
      label={label}
      required={required}
      error={error}
    >
      <Input
        type={fieldType === "email" ? "email" : "text"}
        size="sm"
        variant="bordered"
        value={(normalizedValue ?? "") as string}
        onChange={(e) =>
          handleChange(e.target.value === "" ? undefined : e.target.value)
        }
        maxLength={field.maxLength}
        placeholder={tr(field.placeholder)}
        isInvalid={!!error}
        classNames={inputClassNames}
      />
    </FieldWrapper>
  );
}

export function renderNestedGroup(
  stepId: string,
  field: FieldDefinition,
  stepData: FormValues,
  helpers?: FieldHelpers,
): React.ReactNode {
  const tr = helpers?.tr ?? ((text?: string) => text ?? "");

  const groupValue = ((helpers?.getFormValue
    ? helpers.getFormValue(stepId, field.key)
    : stepData?.[field.key]) ?? {}) as Record<string, FormValue>;

  const handleSubFieldChange = (subKey: string, value: FormValue) =>
    helpers?.updateFieldValue?.(stepId, `${field.key}.${subKey}`, value);

  return (
    <div
      key={field.key}
      className="col-span-full border-t border-slate-100 pt-4 mt-1"
    >
      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
        {tr(field.label || field.key)}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        {(field.fields ?? []).map((subField) =>
          renderSingleField(
            stepId,
            subField,
            stepData,
            groupValue,
            handleSubFieldChange,
            helpers,
          ),
        )}
      </div>
    </div>
  );
}